import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import ar from "./locales/ar.json";
import en from "./locales/en.json";

export type AppLang = "ar" | "en";

export const SUPPORTED_LANGS: AppLang[] = ["ar", "en"];
export const DEFAULT_LANG: AppLang = "ar";

export const isAppLang = (value: unknown): value is AppLang =>
    typeof value === "string" && (SUPPORTED_LANGS as string[]).includes(value);

/**
 * Detect the language to bootstrap i18next with. Same order as the
 * pre-hydration script in index.html so the two stay in sync:
 * URL `/:lang/` segment → localStorage → navigator.language → default.
 * This lets pages rendered outside the LanguageProvider (login, register)
 * still pick the correct language.
 */
const detectInitialLang = (): AppLang => {
    if (typeof window === "undefined") return DEFAULT_LANG;
    try {
        const segment = window.location.pathname.split("/")[1]?.toLowerCase();
        if (isAppLang(segment)) return segment;
        const stored = window.localStorage.getItem("trbd.lang");
        if (isAppLang(stored)) return stored;
        const nav = window.navigator.language?.slice(0, 2).toLowerCase();
        if (isAppLang(nav)) return nav;
    } catch {
        // ignore and fall through
    }
    return DEFAULT_LANG;
};

i18n.use(initReactI18next).init({
    resources: {
        ar: { translation: ar },
        en: { translation: en },
    },
    lng: detectInitialLang(),
    fallbackLng: DEFAULT_LANG,
    interpolation: { escapeValue: false },
    returnNull: false,
});

export default i18n;
